import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Plus, Edit2, Trash2, Star, ChevronLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProviderDoctors() {
  const { api } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    crm: '',
    crm_state: '',
    specialty_id: '',
    subspecialty: '',
    bio: '',
    private_price: '',
    appointment_duration: 30,
  });

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [doctorsRes, specialtiesRes] = await Promise.all([
        api.get('/provider/doctors'),
        axios.get(`${API_URL}/api/public/specialties`)
      ]);
      setDoctors(doctorsRes.data);
      setSpecialties(specialtiesRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (doctor = null) => {
    if (doctor) {
      setEditingDoctor(doctor);
      setFormData({
        full_name: doctor.full_name || '',
        crm: doctor.crm || '',
        crm_state: doctor.crm_state || '',
        specialty_id: doctor.specialty_id || '',
        subspecialty: doctor.subspecialty || '',
        bio: doctor.bio || '',
        private_price: doctor.private_price?.toString() || '',
        appointment_duration: doctor.appointment_duration || 30,
      });
    } else {
      setEditingDoctor(null);
      setFormData({
        full_name: '',
        crm: '',
        crm_state: '',
        specialty_id: '',
        subspecialty: '',
        bio: '',
        private_price: '',
        appointment_duration: 30,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.full_name || !formData.crm || !formData.crm_state || !formData.specialty_id) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const data = {
        ...formData,
        private_price: formData.private_price ? parseFloat(formData.private_price) : null,
      };

      if (editingDoctor) {
        await api.put(`/provider/doctors/${editingDoctor.id}`, data);
        toast.success('Médico atualizado com sucesso');
      } else {
        await api.post('/provider/doctors', data);
        toast.success('Médico cadastrado com sucesso');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar médico');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja remover este médico?')) return;

    try {
      await api.delete(`/provider/doctors/${id}`);
      toast.success('Médico removido');
      fetchData();
    } catch (error) {
      toast.error('Erro ao remover médico');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="provider-doctors">
      <Navbar />

      <div className="container-app py-8">
        <Link to="/prestador" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Link>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Médicos</h1>
            <p className="text-slate-600 mt-1">Gerencie os médicos da sua clínica</p>
          </div>
          <Button onClick={() => handleOpenDialog()} className="bg-primary" data-testid="add-doctor-btn">
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Médico
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse h-32 bg-white rounded-2xl"></div>
            ))}
          </div>
        ) : doctors.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-4">
            {doctors.map((doctor) => (
              <div key={doctor.id} className="bg-white rounded-2xl border border-slate-100 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center">
                      <Users className="w-7 h-7 text-slate-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-lg">{doctor.full_name}</h3>
                      <p className="text-slate-600">{doctor.specialty_name}</p>
                      <p className="text-sm text-slate-500">CRM {doctor.crm}/{doctor.crm_state}</p>
                      {doctor.rating_avg > 0 && (
                        <div className="flex items-center gap-1 mt-2">
                          <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                          <span className="text-sm font-medium">{doctor.rating_avg.toFixed(1)}</span>
                        </div>
                      )}
                      {doctor.private_price && (
                        <Badge className="mt-2">R$ {doctor.private_price.toFixed(2)}</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenDialog(doctor)}
                      data-testid={`edit-doctor-${doctor.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => handleDelete(doctor.id)}
                      data-testid={`delete-doctor-${doctor.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Nenhum médico cadastrado</h3>
            <p className="text-slate-600 mb-6">Comece adicionando os médicos da sua equipe</p>
            <Button onClick={() => handleOpenDialog()} className="bg-primary">
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Médico
            </Button>
          </div>
        )}
      </div>

      {/* Doctor Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingDoctor ? 'Editar Médico' : 'Novo Médico'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome Completo *</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="Dr. Nome Sobrenome"
                data-testid="doctor-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>CRM *</Label>
                <Input
                  value={formData.crm}
                  onChange={(e) => setFormData({ ...formData, crm: e.target.value })}
                  placeholder="123456"
                  data-testid="doctor-crm-input"
                />
              </div>
              <div className="space-y-2">
                <Label>UF do CRM *</Label>
                <Select value={formData.crm_state} onValueChange={(v) => setFormData({ ...formData, crm_state: v })}>
                  <SelectTrigger data-testid="doctor-state-select">
                    <SelectValue placeholder="UF" />
                  </SelectTrigger>
                  <SelectContent>
                    {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map((uf) => (
                      <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Especialidade *</Label>
              <Select value={formData.specialty_id} onValueChange={(v) => setFormData({ ...formData, specialty_id: v })}>
                <SelectTrigger data-testid="doctor-specialty-select">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {specialties.map((specialty) => (
                    <SelectItem key={specialty.id} value={specialty.id}>
                      {specialty.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Subespecialidade</Label>
              <Input
                value={formData.subspecialty}
                onChange={(e) => setFormData({ ...formData, subspecialty: e.target.value })}
                placeholder="Ex: Cardiologia Pediátrica"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Preço Particular (R$)</Label>
                <Input
                  type="number"
                  value={formData.private_price}
                  onChange={(e) => setFormData({ ...formData, private_price: e.target.value })}
                  placeholder="250.00"
                  data-testid="doctor-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Duração Consulta (min)</Label>
                <Input
                  type="number"
                  value={formData.appointment_duration}
                  onChange={(e) => setFormData({ ...formData, appointment_duration: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Mini Bio</Label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Breve descrição do profissional..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} className="bg-primary" data-testid="save-doctor-btn">
              {editingDoctor ? 'Salvar' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
